import {
  cloneElement,
  isValidElement,
  useId,
  useState,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cx } from '../cx';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** A single element that can receive hover and keyboard focus. */
  children: ReactElement<Record<string, unknown>>;
}

/**
 * Shows `content` on hover AND on keyboard focus of the child — never
 * hover-only, so the information isn't lost to anyone who navigates by
 * keyboard. The child keeps whatever handlers it already had; this only adds
 * to them.
 */
export function Tooltip({ content, placement = 'top', children }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();

  if (!isValidElement(children)) return children;

  const childProps = children.props as Record<string, unknown> & {
    onMouseEnter?: (e: MouseEvent) => void;
    onMouseLeave?: (e: MouseEvent) => void;
    onFocus?: (e: FocusEvent) => void;
    onBlur?: (e: FocusEvent) => void;
    'aria-describedby'?: string;
  };

  const trigger = cloneElement(children, {
    onMouseEnter: (e: MouseEvent) => {
      childProps.onMouseEnter?.(e);
      setOpen(true);
    },
    onMouseLeave: (e: MouseEvent) => {
      childProps.onMouseLeave?.(e);
      setOpen(false);
    },
    onFocus: (e: FocusEvent) => {
      childProps.onFocus?.(e);
      setOpen(true);
    },
    onBlur: (e: FocusEvent) => {
      childProps.onBlur?.(e);
      setOpen(false);
    },
    'aria-describedby': open
      ? [childProps['aria-describedby'], id].filter(Boolean).join(' ')
      : childProps['aria-describedby'],
  });

  return (
    <span
      className={styles.wrapper}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      {trigger}
      {open && (
        <span role="tooltip" id={id} className={cx(styles.tooltip, styles[placement])}>
          {content}
        </span>
      )}
    </span>
  );
}
